import React, { useEffect, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, User, X } from "lucide-react";
import { toast } from "sonner";

// Step 1 — live guest search, then pick exactly ONE treatment this guest had.
export const GoalGuestStep = ({ guest, setGuest, treatment, setTreatment, lockGuest = false }) => {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (guest || q.trim().length < 2) { setHits([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await staffApi.get("/customers", { params: { search: q.trim(), per_page: 12 } });
        setHits(r.data?.rows || []);
      } catch { /* silent */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q, guest]);

  useEffect(() => {
    if (!guest) { setTreatments([]); return; }
    (async () => {
      setLoading(true);
      try {
        const r = await staffApi.get(`/goal/customer-treatments/${guest.id}`);
        setTreatments(r.data?.treatments || []);
      } catch (e) { toast.error(formatApiError(e)); }
      setLoading(false);
    })();
  }, [guest]);

  if (guest) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2">
          <div>
            <p className="text-sm font-medium" data-testid="goal-selected-guest">{guest.full_name || `${guest.first_name || ""} ${guest.last_name || ""}`.trim()}</p>
            <p className="text-xs text-muted-foreground">{guest.whatsapp || "—"} · {guest.email || "—"}</p>
          </div>
          <Button
            size="sm" variant="ghost"
            onClick={() => { setGuest(null); setTreatment(null); setQ(""); }}
            disabled={lockGuest}
            className={lockGuest ? "hidden" : ""}
            data-testid="goal-clear-guest"
          >
            <X className="w-4 h-4 mr-1" /> Change
          </Button>
        </div>

        <div>
          <p className="text-sm font-medium mb-1.5">Which treatment is this about?</p>
          {loading && <p className="text-sm text-muted-foreground">Loading treatments…</p>}
          {!loading && treatments.length === 0 && (
            <p className="text-sm text-muted-foreground">This guest has no treatments on file yet.</p>
          )}
          <div className="max-h-[260px] overflow-y-auto divide-y divide-border rounded-md border border-border">
            {treatments.map((t) => {
              const picked = treatment?.appointment_id === t.appointment_id;
              return (
                <button
                  key={t.appointment_id}
                  type="button"
                  onClick={() => setTreatment(t)}
                  className={`w-full text-left px-3 py-2 transition-colors ${picked ? "bg-primary/10" : "hover:bg-muted/60"}`}
                  data-testid={`goal-treatment-option-${t.appointment_id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm">{t.treatment_name}</span>
                    {picked && <Badge variant="secondary" className="text-[10px]">Selected</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t.treatment_date} · {t.branch_name} · {t.practitioner_name} · {t.status}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search guest by name…"
          className="pl-9"
          autoFocus
          data-testid="goal-guest-search"
        />
      </div>
      {q.trim().length >= 2 && (
        <div className="max-h-[300px] overflow-y-auto divide-y divide-border rounded-md border border-border">
          {hits.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { setGuest(c); setHits([]); }}
              className="w-full text-left px-3 py-2 hover:bg-muted/60 transition-colors"
              data-testid={`goal-guest-option-${c.id}`}
            >
              <p className="text-sm font-medium flex items-center gap-2">
                <User className="w-3.5 h-3.5 text-muted-foreground" />
                {c.full_name || `${c.first_name || ""} ${c.last_name || ""}`.trim()}
              </p>
              <p className="text-xs text-muted-foreground pl-5">{c.whatsapp || "—"} · {c.email || "—"}</p>
            </button>
          ))}
          {hits.length === 0 && <p className="px-3 py-6 text-sm text-muted-foreground text-center">No guest found.</p>}
        </div>
      )}
    </div>
  );
};

export default GoalGuestStep;
